"use client";

import { useState, useCallback } from "react";
import { PANEL } from "@/config/constants";

const QUICK_PICKS = [
  "google.com",
  "github.com",
  "netflix.com",
  "bbc.co.uk",
  "alibaba.com",
];

function isValidDomain(input: string): boolean {
  const trimmed = input.trim().replace(/^https?:\/\//, "").split("/")[0];
  return /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/.test(trimmed);
}

interface HeroInputProps {
  startTrace: (domain: string) => void;
  traceError: string | null;
  onDismissError: () => void;
}

export default function HeroInput({ startTrace, traceError, onDismissError }: HeroInputProps) {
  const [urlInput, setUrlInput] = useState("");
  const [urlError, setUrlError] = useState(false);

  const handleUrlSubmit = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      if (!isValidDomain(trimmed)) {
        setUrlError(true);
        return;
      }
      setUrlError(false);
      const domain = trimmed.replace(/^https?:\/\//, "").split("/")[0];
      startTrace(domain);
    },
    [startTrace]
  );

  return (
    <div
      id="main-content"
      className="absolute top-[38%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-5 sm:gap-7 w-full max-w-xl px-4 sm:px-6"
      role="main"
    >
      {/* Trace error banner */}
      {traceError && (
        <div
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-[13px]"
          role="alert"
          style={{
            background: "rgba(244,63,94,0.08)",
            border: "1px solid rgba(244,63,94,0.2)",
          }}
        >
          <svg
            className="w-4 h-4 text-[#F43F5E] shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-[#F43F5E] flex-1">{traceError}</span>
          <button
            onClick={onDismissError}
            className="text-[#F43F5E]/60 hover:text-[#F43F5E] transition-colors cursor-pointer"
            aria-label="Dismiss error"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="text-center space-y-4">
        <h1
          className="text-[36px] sm:text-[52px] font-extrabold tracking-[-0.02em]"
          style={{ fontFamily: "var(--font-plus-jakarta)" }}
        >
          <span className="text-[#E4E4E7]">Packet</span>
          <span className="text-[#06B6D4]">Voyager</span>
        </h1>
        <p className="text-[#A1A1AA] text-[14px] sm:text-[16px] leading-relaxed">
          Watch your internet request travel across the planet
        </p>
      </div>

      <div className="relative w-full group">
        <div className="absolute inset-0 rounded-xl bg-[#06B6D4]/5 blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" />
        <div
          className="relative flex items-center gap-3 px-5 py-4.5 rounded-xl"
          style={{
            background: PANEL.BG,
            border: `1px solid ${urlError ? "rgba(244,63,94,0.5)" : PANEL.BORDER}`,
            boxShadow: `0 0 0 1px rgba(255,255,255,0.03), ${PANEL.SHADOW_HEAVY}`,
          }}
        >
          <svg
            className="w-5 h-5 text-[#71717A] shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <label htmlFor="url-input" className="sr-only">
            URL to trace
          </label>
          <input
            id="url-input"
            type="text"
            value={urlInput}
            onChange={(e) => {
              setUrlInput(e.target.value);
              setUrlError(false);
            }}
            placeholder="Enter any URL to trace its journey..."
            className="flex-1 bg-transparent text-[#F4F4F5] text-[15px] placeholder:text-[#71717A] outline-none"
            style={{ fontFamily: "var(--font-geist-sans)" }}
            aria-invalid={urlError}
            aria-describedby={urlError ? "url-error" : undefined}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleUrlSubmit(urlInput);
              }
            }}
          />
          <kbd
            className="hidden sm:flex items-center px-2 py-1 rounded-md text-[10px] text-[#71717A] border border-[rgba(255,255,255,0.08)]"
            aria-hidden="true"
          >
            &#x23CE;
          </kbd>
        </div>
        {urlError && (
          <p id="url-error" className="absolute -bottom-6 left-0 text-[11px] text-[#F43F5E]" role="alert">
            Please enter a valid domain (e.g. google.com)
          </p>
        )}
      </div>

      <nav className="flex items-center gap-2 flex-wrap sm:flex-wrap justify-center overflow-x-auto max-w-full" aria-label="Quick-pick domains">
        <span className="text-[11px] text-[#71717A] tracking-wider uppercase mr-1 shrink-0">Try:</span>
        {QUICK_PICKS.map((domain) => (
          <button
            key={domain}
            onClick={() => startTrace(domain)}
            aria-label={`Trace ${domain}`}
            className="px-3 py-1.5 rounded-full text-[13px] text-[#A1A1AA] hover:text-[#F4F4F5] bg-[#18181B] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.16)] hover:bg-[#27272A] transition-all duration-200 cursor-pointer font-[family-name:var(--font-geist-mono)]"
          >
            {domain}
          </button>
        ))}
      </nav>
    </div>
  );
}
